import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { AbstractPanelManagementDataSource, IAbstractFormDataSource, ISelectOption } from '@types-custom/models/ui/paginator-model';
import { BehaviorSubject, Subject } from 'rxjs';

export enum InputTypeEnum {
  ACTION_BUTTON = 'action-button',
  AUTOCOMPLETE_INPUT = 'autocomplete',
  BUTTON_CHECKBOX = 'button-checkbox',
  BUTTON_RADIO = 'button-radio',
  CHIP = 'chip',
  DATE = 'date',
  DATETIME = 'datetime',
  INPUT = 'text',
  INPUT_NUMBER = 'number',
  LATITUD_CODE_INPUT = 'latitudCode',
  LOCATION_INPUT = 'address',
  LOCATION_INPUT_KEY = 'location',
  LONGITUD_CODE_INPUT = 'longitudCode',
  POSTAL_CODE_INPUT = 'postalCode',
  SELECT = 'select',
  TEXT = 'textarea',
  TIME = 'time',
  TOOGLESWITCH = 'toogleSwitch',
  LIST = 'list',
  BINARY = 'binary',

  TESTING_RADIO = 'testing-radio',
}

export enum FormGroupNamesEnum {
  MAIN = 'mainForm',
  OPTIONAL = 'OptionalForm',
  INCIDENT_TYPE = 'incidentTypeId',
  OPTIONAL_DEPENDENT = 'optionalDependent',
}

export interface ParametricFormI {

  /**
    * Type of the input control (InputTypeEnum), such as text, select, etc.
    */
  type: InputTypeEnum;

  /**
    * Unique key to identify the control within the form.
    */
  formKey: string;

  /**
    * Descriptive label for the form field.
    */
  label: string;

  /**
    * Name of the form to which this control belongs.
    */
  formName?: string;

  /**
    * Configuration of the form control, including its current value, available options, and applicable validators.
    */
  formControl: {
    value: string | string[] ;
    options: any;
    validators?: IValidatorModel[];
  };

  /**
     * Type of list options, typically referring to the data source.
     */
  listOptionsType?: string;
  /**
     * Decides if preloads the listOptionsType to avoid empty list at select controls
     */
  // setDefaultList?: boolean;
  /**
     * Static list of options for a control, used in drowpdownds and chips (checkbox and radio-buttons).
     */
  list?: { value: string, name: string }[];

  /**
   * Optional list of sub-items for fields with options for autocompletes.
   */
  subList?: { value: string; name: string }[];

  /**
   * Indicates whether the list options should be cached for future use.
   */
  cacheList?: boolean;

  /**
   * Optional function to determine the visual representation of the selected value in an autocomplete field.
   */
  autoCompleteDisplayFn?: ((value: any) => string) | null;

  /**
   * Optional function to dynamically obtain autocomplete options.
   */
  autoCompleteObservableFn?: ((value: any) => void) | null;

  /**
   * Indicates whether the label should be used as the control's value.
   */
  turnLabelAsValue?: boolean;

  /**
   * Determines if the option labels should be sorted (alphabetically).
   */
  sortLabels?: boolean;

  /**
   * Indicates if the control is dynamically generated during runtime (From API).
   */
  isDynamicControl?: boolean;

  /**
   * Type of parent list options, used to check the cache for autocomplete lists.
   */
  autocompleteParentList?: string;

  /**
   * Key of the parent form, if the current control visualization and values depends on another form.
   */
  parentFormKey?: string;

  /**
   * Determines if the current control is enabled to be visible by its parent value
   */
  parentEnabled?: boolean;

  /**
   * Name of the parent form, if applicable, given a form with multiple formgroups.
   */
  parentFormName?: string;

  /**
   * Configuration for fields that depend on the value of an ancestor control in the form hierarchy.
   */
  ancestorValueDependant?: {
    key: string,
    type: string,
  }

  /**
   * Adds a step verification for visibility where the function defines its status
   */
  visibilityFn?: () => boolean;

  /**
   * Indicates whether the control should be hidden in the user interface by default.
   */
  hiddenControl?: boolean;

  /**
   * Indicates whether the control's label should be hidden.
   */
  hiddenLabel?: boolean;

  /**
   * Identifier for a control that is part of a dynamic list within the form.
   */
  dynamicInsideControlList?: string;

  /**
 * Model for forms that depend on other controls or forms.
 */
  dependentFormModel?: ParametricFormI[][];
  /**
   * Indicates whether this form is optional within its own group of forms.
   */
  isOptionalFormInSelfGroup?: boolean;

  /**
   * Indicates whether the optionality of this form depends on another form in the same group.
   */
  isOptionalFormDependentInSelfGroup?: boolean;

  /**
   * Indicates whether the control supports multiple selectable values.
   */
  multipleFormControl?: boolean;

  /**
   * Model for controlling forms with multiple input controls, structured in a matrix.
   */
  multipleFormControlModel?: ParametricFormI[][];

  /**
   * Conditional map that defines the appearance of forms based on certain conditions or values of other controls.
   */
  conditionalFormMap?: { [key: string]: ParametricFormI[][] };

  /**
   * Function to handle changes in the control's value. Used to apply custom logic when the value changes.
   */
  changeValueFn?: (control: AbstractControl) => (params?: any) => any;

  /**
   * Tabulation number or index associated with the control in the form, used for keyboard navigation.
   */
  tab?: number;

}

export interface IParametricControl {
  type: InputTypeEnum;
  key: string;
  label: string;
  formControl?: {
    value: string | string[];
    options: any;
    validators?: IValidatorModel[];
  };
  ancestorDependant?: string[];

  listType: string;
  list: ISelectOption[];
  cacheList?: boolean;
  // inputProps: TypeInputProps;
  visibility?: boolean; //By default should be true
}

export interface IGenericFormModel {
  serviceDataSource: AbstractPanelManagementDataSource;
  geolocationDataSource?: any;
  formGroup: UntypedFormGroup;
  formModel: ParametricFormI[][];
  actionDataForm?: Subject<any>;
  actionPanel$: BehaviorSubject<any>;
  panelOnManagement: string;
}
export interface IModuleFormModel<T> {
  serviceDataSource: AbstractPanelManagementDataSource<T>;
  formGroup: FormGroup;
  formModel: IParametricControl[][];
}

export enum CodeValidatorNameEnum {
  REQUIRED             = 'required',
  MIN_LENGTH_VALIDATOR = 'minlength',
  MAX_LENGTH_VALIDATOR = 'maxlength',
  MAX_VALIDATOR        = 'max',
  SAME_VALIDATOR       = 'sameValueValidator',
  EMAIL_VALIDATOR      = 'email',
  SPECIAL_CHARACTERS   = 'specialCharacters',
  ONLY_NUMBERS         = 'onlyNumbers',
  NUMBERS_CHARACTERS   = 'numberCharacters',
  COMPARE_DATETIMES    = 'compareDateTimeValidator',
  GREATER_DATE         = 'greaterThanDateValidator',
  LESS_DATE            = 'lessThanDateValidator',
  COMPARE_VALUE_KEY    = 'compareValueKey',
  OPTION_AUTOCOPLETE   = 'autocompleteValueKey',
  NOT_START_WITH_SPACE = 'notSpaceStart',
}

export interface IValidatorModel {
  validator: ValidationErrors;
  validatorCodeName: CodeValidatorNameEnum;
  errorMessage: string;
}

export const SameValueValidator = (
  controlName: string,
  matchingControlName: string
) => {
  return (formGroup: FormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    if (
      matchingControl?.errors &&
      !matchingControl?.errors[`${CodeValidatorNameEnum.SAME_VALIDATOR}`]
    ) {
      return;
    }
    if (control?.value !== matchingControl?.value) {
      const error = { [CodeValidatorNameEnum.SAME_VALIDATOR]: true };
      matchingControl.setErrors(error);
    } else {
      matchingControl.setErrors(null);
    }
  };
};

export function validarValorAutocomplete(opcionesValidas: {
  value: number;
  name: string;
}[]) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!opcionesValidas || !opcionesValidas.length) return null;
    if (opcionesValidas.length && (!control.value)) return null;
    const valor = control.value;
    const esValido = opcionesValidas.some(opcion => opcion.value === valor || opcion.name === valor);

    return (esValido) ? null : { [CodeValidatorNameEnum.OPTION_AUTOCOPLETE]: true };
  };
}

export const specialCharactersValidator = (control: FormControl) => {
  // eslint-disable-next-line no-useless-escape
  const specialCharactersRegexp = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
  if (control.value && specialCharactersRegexp.test(control.value)) {
    const error = { [CodeValidatorNameEnum.SPECIAL_CHARACTERS]: true };
    return error;
  }
  return null;
};

export const notSpaceStartOrEndValidator = (control: FormControl) => {
  // Regular expression to check for no leading or trailing spaces
  const noSpaceStartOrEndRegexp = /^[^\s].*[^\s]$/;
  if (control.value && !noSpaceStartOrEndRegexp.test(control.value)) {
    return {  [CodeValidatorNameEnum.NOT_START_WITH_SPACE]: true };
  }
  return null;
};

export const onlyNumbersValidator = (control: FormControl) => {
  // eslint-disable-next-line no-useless-escape
  const onlyNumbersRegexp = /^[0-9]*$/;
  if (control.value && !onlyNumbersRegexp.test(control.value)) {
    const error = { [CodeValidatorNameEnum.ONLY_NUMBERS]: true };
    return error;
  }
  return null;
};

export const numberCharactersValidator = (control: FormControl) => {
  // eslint-disable-next-line no-useless-escape
  const numberCharactersRegexp = /^(?!.*\.\..*)[0-9.]+$/;
  if (control.value && !numberCharactersRegexp.test(control.value)) {
    const error = { [CodeValidatorNameEnum.NUMBERS_CHARACTERS]: true };
    return error;
  }
  return null;
};

export const greaterThanDateValidator = (nameControlDateCompare: string) => (control: AbstractControl) => {
  const dateOne = control?.value;
  const dateTwoControl = control?.parent?.get(nameControlDateCompare);
  const dateTwo = dateTwoControl?.value;


  if (dateOne && dateTwo) {
    if (dateOne > dateTwo) {
      const error = { [CodeValidatorNameEnum.GREATER_DATE]: true };
      return error;
    }
  }

  return null;
}

export const lessThanDateValidator = (nameControlDateCompare: string) => (control: AbstractControl) => {
  const dateOne = control?.value;
  const dateTwoControl = control?.parent?.get(nameControlDateCompare);
  const dateTwo = dateTwoControl?.value;

  if (dateOne && dateTwo) {
    if (dateOne < dateTwo) {
      const error = { [CodeValidatorNameEnum.LESS_DATE]: true };
      return error;
    }
  }
  return null;
}

export const compareValueKeyValidator = (nameControl: string, codeCompare: string) => (control: AbstractControl) => {
  const dateOne = control?.value;
  const dateTwoControl = control?.parent?.get(nameControl);
  const dateTwo = dateTwoControl?.value;
  const currentDate = new Date();

  if (dateOne) {
    if (dateTwo === codeCompare) {
      if (dateOne > currentDate) {
        const error = { [CodeValidatorNameEnum.COMPARE_VALUE_KEY]: true };
        return error;
      }
    }
  }

  return null;
}

